import type {
  EffectivePermission,
  PermissionRequest,
  PermissionScope,
  RolePermissionRecord,
} from './rolePermission.types'

type ResolvePermissionOptions = PermissionRequest & {
  companyId?: string
  permissions: RolePermissionRecord[]
  roleCode?: string
}

const VALID_SCOPES: PermissionScope[] = ['all', 'own', 'assigned', 'none']

function normalize(value?: string | null) {
  return value?.trim().toLowerCase() ?? ''
}

function getValidScopes(permission: RolePermissionRecord) {
  return (permission.scope ?? []).filter((scope) =>
    VALID_SCOPES.includes(scope)
  )
}

export function resolvePermission({
  action,
  companyId,
  module,
  permissions,
  roleCode,
}: ResolvePermissionOptions): EffectivePermission {
  if (!roleCode) {
    return { allowed: false, scopes: [] }
  }

  const matchingPermissions = permissions.filter((permission) => {
    const isApplicableCompany =
      permission.company_id === null || permission.company_id === companyId

    return (
      isApplicableCompany &&
      normalize(permission.role_code) === normalize(roleCode) &&
      normalize(permission.module) === normalize(module) &&
      normalize(permission.action) === normalize(action)
    )
  })

  if (normalize(roleCode) === 'admin' && !matchingPermissions.length) {
    return { allowed: true, scopes: ['all'] }
  }

  const companyPermissions = companyId
    ? matchingPermissions.filter(
        (permission) => permission.company_id === companyId
      )
    : []
  const fallbackPermissions = matchingPermissions.filter(
    (permission) => permission.company_id === null
  )

  // company_id: null is the fallback for the matching role/module/action.
  // A company-level rule replaces that fallback for the same role/module/action.
  const effectivePermissions = companyPermissions.length
    ? companyPermissions
    : fallbackPermissions
  const denied = effectivePermissions.some((permission) => {
    const scopes = getValidScopes(permission)

    return normalize(permission.effect) === 'deny' || scopes.includes('none')
  })

  if (denied) {
    return { allowed: false, scopes: [] }
  }

  const allowedScopes = new Set<PermissionScope>()
  effectivePermissions.forEach((permission) => {
    if (normalize(permission.effect) !== 'allow') return

    getValidScopes(permission).forEach((scope) => {
      if (scope !== 'none') {
        allowedScopes.add(scope)
      }
    })
  })

  if (allowedScopes.has('all')) {
    return { allowed: true, scopes: ['all'] }
  }

  const scopes = Array.from(allowedScopes)

  return {
    allowed: scopes.length > 0,
    scopes,
  }
}

export function isPermissionAllowed(options: ResolvePermissionOptions) {
  const permission = resolvePermission(options)

  if (!permission.allowed) return false
  if (permission.scopes.includes('all')) return true

  // Module-level checks, such as showing a route or create button, only need
  // an allowed action. Resource checks must match own/assigned scopes.
  if (!options.resourceScopes) return true

  return options.resourceScopes.some((scope) =>
    permission.scopes.includes(scope)
  )
}
